//Users/aminb101/iuhimporter/iuh-importer/frontend/src/pages/StoreConnectionPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { API_URL, api } from '../utils/api';
import { redirectToUrl } from '../utils/shopifyAppBridge';

const StoreConnectionPage = () => {
  const [stores, setStores] = useState([]); // Used in fetchStores
  const [gaProperties, setGaProperties] = useState({});
  const [selectedGA, setSelectedGA] = useState({});
  const [gscSites, setGscSites] = useState({});
  const [selectedGSC, setSelectedGSC] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({
    ga: false,
    gsc: false
  });
  const [isLoading, setIsLoading] = useState({
    stores: true,
    ga: false,
    gsc: false,
    saveGA: false,
    saveGSC: false
  });
  const [message, setMessage] = useState({
    type: '',
    text: ''
  });

  const fetchGAProperties = useCallback(async (storeId) => {
    try {
      setIsLoading(prev => ({ ...prev, ga: true }));
      const res = await api.get(`/api/google/test-connection?storeId=${storeId}`);
      const data = res.parsedBody;

      if (!data.connected) {
        setConnectionStatus(prev => ({ ...prev, ga: false }));
        setMessage({
          type: 'info',
          text: 'Please connect your Google account to access Analytics'
        });
        return;
      }

      // If connected, fetch GA properties
      const gaRes = await api.get(`/api/google/analytics/properties?storeId=${storeId}`);
      const gaData = gaRes.parsedBody;
      setGaProperties(prev => ({ ...prev, [storeId]: Array.isArray(gaData) ? gaData : (gaData.properties || []) }));
      setConnectionStatus(prev => ({ ...prev, ga: true }));

    } catch (err) {
      console.error('Failed to fetch GA properties:', err);
      setMessage({
        type: 'error',
        text: `Error: ${err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, ga: false }));
    }
  }, []);

  // Similar update for fetchGSCSites
  const fetchGSCSites = useCallback(async (storeId) => {
    try {
      setIsLoading(prev => ({ ...prev, gsc: true }));
      const res = await api.get(`/api/google/test-connection?storeId=${storeId}`);
      const data = res.parsedBody;

      if (!data.connected) {
        setConnectionStatus(prev => ({ ...prev, gsc: false }));
        setMessage({
          type: 'info',
          text: 'Please connect your Google account to access Search Console'
        });
        return;
      }

      // If connected, fetch GSC sites
      const gscRes = await api.get(`/api/google/search-console/sites?storeId=${storeId}`);
      const gscData = gscRes.parsedBody;
      setGscSites(prev => ({ 
        ...prev, 
        [storeId]: Array.isArray(gscData) ? gscData : (gscData.sites || []) 
      }));
      setConnectionStatus(prev => ({ ...prev, gsc: true }));

    } catch (err) {
      console.error('Failed to fetch GSC sites:', err);
      setMessage({
        type: 'error',
        text: `Error: ${err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, gsc: false }));
    }
  }, []);

  const checkExistingConnections = useCallback(async (storeId) => {
    try {
      const res = await api.get(`/api/stores/${storeId}/google-settings`);
      const data = res.parsedBody || (await res.json());

      // If there's an existing GA property ID, set it in state and fetch available properties
      if (data.gaPropertyId) {
        setSelectedGA(prev => ({ ...prev, [storeId]: data.gaPropertyId }));
        setConnectionStatus(prev => ({ ...prev, ga: true }));
        fetchGAProperties(storeId);
      }
      
      // If there's an existing GSC site URL, set it in state and fetch available sites
      if (data.gscSiteUrl) {
        setSelectedGSC(prev => ({ ...prev, [storeId]: data.gscSiteUrl }));
        setConnectionStatus(prev => ({ ...prev, gsc: true }));
        fetchGSCSites(storeId);
      }
    } catch (err) {
      console.error('Failed to check existing connections:', err);
    }
  }, [fetchGAProperties, fetchGSCSites]);

  const fetchStores = useCallback(async () => {
    try {
      setIsLoading(prev => ({ ...prev, stores: true }));
      // Use authenticated client so Authorization header is sent
      const res = await api.get('/api/stores');
      const data = res.parsedBody || await res.json();
      
      if (Array.isArray(data)) {
        setStores(data);
        // Initialize connections for each store
        data.forEach(store => checkExistingConnections(store.id));
      } else {
        throw new Error('Invalid response format from stores endpoint');
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to load stores. Please try again.'
      });
    } finally {
      setIsLoading(prev => ({ ...prev, stores: false }));
    }
  }, [checkExistingConnections]);
  
  // Fetch stores on initial load and when API_URL changes
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Add manual refresh button
  const handleRefreshStores = useCallback(() => {
    fetchStores();
  }, [fetchStores]);

  // Check connection status from URL params
  useEffect(() => {
    const url = new URL(window.location.href);
    const connection = url.searchParams.get('connection');
    const storeId = url.searchParams.get('storeId');
    
    if (connection === 'google_success' && storeId) {
      setMessage({
        type: 'success',
        text: 'Successfully connected to Google. Now you can select your GA property.'
      });
      fetchGAProperties(storeId);
      setConnectionStatus(prev => ({ ...prev, ga: true }));
    } else if (connection === 'gsc_success' && storeId) {
      setMessage({
        type: 'success',
        text: 'Successfully connected to Google Search Console. Now you can select your site.'
      });
      fetchGSCSites(storeId);
      setConnectionStatus(prev => ({ ...prev, gsc: true }));
    } else if (connection === 'google_error') {
      setMessage({
        type: 'error',
        text: 'Failed to connect to Google services. Please try again.'
      });
    }
  }, [fetchGAProperties, fetchGSCSites]);

  const handleInitiateShopifyAuth = () => {
    // Check if we're in an embedded context (shop parameter in URL)
    const urlParams = new URLSearchParams(window.location.search);
    let shopDomain = urlParams.get('shop');
    
    // If not embedded, prompt for shop domain
    if (!shopDomain) {
      shopDomain = prompt('Enter your Shopify store domain (e.g., mystore.myshopify.com):');
      if (!shopDomain) {
        setMessage({
          type: 'error',
          text: 'Shop domain is required to connect your Shopify store.'
        });
        return;
      }
      
      // Normalize the shop domain
      shopDomain = shopDomain.trim().toLowerCase();
      if (!shopDomain.includes('.myshopify.com')) {
        shopDomain = `${shopDomain}.myshopify.com`;
      }
    }
    
    // Get host parameter if available (for embedded apps)
    const host = urlParams.get('host');
    const authUrl = new URL(`${API_URL}/api/auth/shopify/start`);
    authUrl.searchParams.set('shop', shopDomain);
    if (host) {
      authUrl.searchParams.set('host', host);
    }
    
    redirectToUrl(authUrl.toString());
  };

  const handleGAConnect = async (storeId) => {
    try {
      const response = await api.get(`/api/google/test-connection?storeId=${storeId}`);
      const data = response.parsedBody;
      if (data) {
        if (!data.connected) {
          const initiateRes = await api.get(`/api/auth/google/initiate?storeId=${storeId}&source=ga&mode=json`);
          const initiateData = initiateRes.parsedBody || (await initiateRes.json());
          if (!initiateData?.authUrl) {
            throw new Error('Failed to retrieve Google authorization URL');
          }
          redirectToUrl(initiateData.authUrl);
          return;
        }
      }

      // If already connected or error, just fetch properties
      fetchGAProperties(storeId);
    } catch (error) {
      console.error('Error checking Google connection:', error);
      setMessage({
        type: 'error',
        text: 'Failed to check Google connection status'
      });
    }
  };
  
  const handleGSCConnect = async (storeId) => {
    try {
      const response = await api.get(`/api/google/test-connection?storeId=${storeId}`);
      const data = response.parsedBody;
      if (data) {
        if (!data.connected) {
          const initiateRes = await api.get(`/api/auth/google/initiate?storeId=${storeId}&source=gsc&mode=json`);
          const initiateData = initiateRes.parsedBody || (await initiateRes.json());
          if (!initiateData?.authUrl) {
            throw new Error('Failed to retrieve Google authorization URL');
          }
          redirectToUrl(initiateData.authUrl);
          return;
        }
      }

      // If already connected or error, just fetch sites
      fetchGSCSites(storeId);
    } catch (error) {
      console.error('Error checking Google connection:', error);
      setMessage({
        type: 'error',
        text: 'Failed to check Google connection status'
      });
    }
  };

  const handleSaveGA = async (storeId) => {
    if (!selectedGA[storeId]) {
      setMessage({
        type: 'error',
        text: 'Please select a Google Analytics property'
      });
      return;
    }
    
    try {
      setIsLoading(prev => ({ ...prev, saveGA: true }));
      
      const res = await fetch(`${API_URL}/api/stores/${storeId}/google-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          gaPropertyId: selectedGA[storeId],
          gscSiteUrl: selectedGSC[storeId] || null  // Keep the existing GSC selection if any
        }),
        credentials: 'include'
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save GA settings');
      }
      
      setMessage({
        type: 'success',
        text: 'Google Analytics property saved successfully!'
      });
    } catch (err) {
      console.error('Failed to save GA settings:', err);
      setMessage({
        type: 'error',
        text: `Failed to save Google Analytics settings: ${err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, saveGA: false }));
    }
  };

  const handleSaveGSC = async (storeId) => {
    if (!selectedGSC[storeId]) {
      setMessage({
        type: 'error',
        text: 'Please select a Search Console site'
      });
      return;
    }
    
    try {
      setIsLoading(prev => ({ ...prev, saveGSC: true }));
      
      const res = await fetch(`${API_URL}/api/stores/${storeId}/google-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          gscSiteUrl: selectedGSC[storeId],
          gaPropertyId: selectedGA[storeId] || null  // Keep the existing GA selection if any
        }),
        credentials: 'include'
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save GSC settings');
      }
      
      setMessage({
        type: 'success',
        text: 'Search Console site saved successfully!'
      });
    } catch (err) {
      console.error('Failed to save GSC settings:', err);
      setMessage({
        type: 'error',
        text: `Failed to save Search Console settings: ${err.message}`
      });
    } finally {
      setIsLoading(prev => ({ ...prev, saveGSC: false }));
    }
  };
  
  const getCurrentStore = useCallback(() => {
    return stores.length > 0 ? stores[0] : null;
  }, [stores]);



  // ADDED: Debug helper function
  const debugGAProperties = () => {
    const storeId = getCurrentStore()?.id;
    if (!storeId) return null;
    
    console.log("Debug GA Properties:", {
      storeId,
      gaPropertiesForStore: gaProperties[storeId],
      gaPropertiesExists: !!gaProperties[storeId],
      gaPropertiesLength: gaProperties[storeId]?.length,
      selectedGA: selectedGA[storeId]
    });
    
    // Force refetch GA properties
    fetchGAProperties(storeId);
    return null;
  };
  
  return (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Connect Your Stores</h2>
        <p className="text-gray-600 mt-1">Synchronize your ecommerce stores to analyze product performance and keyword metrics</p>
      </div>

      {/* Message display */}
      {message.text && (
        <div className={`mb-4 p-3 rounded ${message.type === 'error' ? 'bg-red-100 border border-red-200 text-red-700' : 'bg-green-100 border border-green-200 text-green-700'}`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Connected Stores Card */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800">Connected Stores</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-indigo-600">
                {isLoading.stores ? 'Loading...' : `${stores.length} Stores`}
              </span>
              <button
                onClick={handleRefreshStores}
                className="text-sm text-gray-500 hover:text-gray-700"
                title="Refresh store list"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>
          <div className="space-y-4">
            {isLoading.stores ? (
              <p className="text-gray-500 text-sm">Loading stores...</p>
            ) : stores.length > 0 ? (
              stores.map((store) => (
                <div key={store.id} className="p-4 bg-green-50 rounded-md border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-white rounded-md flex items-center justify-center">
                        <i className={`fab fa-${store.platform.toLowerCase()} text-2xl`}></i>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-800">{store.name}</h4>
                        <p className="text-xs text-gray-500">Connected</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-1"></span>
                      <span className="text-xs text-gray-600">Active</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No stores connected yet.</p>
            )}
          </div>
        </div>

        {/* Add New Store Card */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Add New Store</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div 
                onClick={handleInitiateShopifyAuth} 
                className="p-4 border border-gray-200 rounded-md hover:border-indigo-500 cursor-pointer transition-all duration-200 flex flex-col items-center"
              >
                <i className="fab fa-shopify text-3xl text-[#96bf48] mb-2"></i>
                <span className="text-sm font-medium text-gray-800">Shopify</span>
              </div>
              <div className="p-4 border border-gray-200 rounded-md hover:border-indigo-500 cursor-pointer transition-all duration-200 flex flex-col items-center">
                <i className="fab fa-woo text-3xl text-[#7f54b3] mb-2"></i>
                <span className="text-sm font-medium text-gray-800">WooCommerce</span>
              </div>
              <div className="p-4 border border-gray-200 rounded-md hover:border-indigo-500 cursor-pointer transition-all duration-200 flex flex-col items-center">
                <i className="fab fa-magento text-3xl text-[#f46f25] mb-2"></i>
                <span className="text-sm font-medium text-gray-800">Magento</span>
              </div>
              <div className="p-4 border border-gray-200 rounded-md hover:border-indigo-500 cursor-pointer transition-all duration-200 flex flex-col items-center">
                <i className="fas fa-square text-3xl text-[#00c1de] mb-2"></i>
                <span className="text-sm font-medium text-gray-800">Square</span>
              </div>
            </div>
            <button 
              onClick={handleInitiateShopifyAuth} 
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors duration-200"
            >
              Connect Store
            </button>
          </div>
        </div>

        {/* API Connections Card */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-800 mb-4">API Connections</h3>
          <div className="space-y-4">
            {stores.length === 0 ? (
              <p className="text-gray-500 text-sm">No stores connected yet.</p>
            ) : (
              <>
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Google Integrations</h4>
                  <div className="flex flex-col gap-2 mb-4">
                    {stores.length === 0 ? (
                      <p className="text-sm text-gray-500">Connect a store first to enable Google integrations</p>
                    ) : (
                      <>
                        <button
                          onClick={() => handleGAConnect(getCurrentStore().id)}
                          className={`text-sm ${connectionStatus.ga ? 'text-green-600 hover:text-green-800' : 'text-indigo-600 hover:text-indigo-800'} text-left flex items-center`}
                          disabled={isLoading.ga || !getCurrentStore()}
                        >
                          {isLoading.ga && <span className="mr-2 animate-spin">⟳</span>}
                          {connectionStatus.ga ? 'Reconnect Google Analytics' : 'Connect Google Analytics'}
                          {connectionStatus.ga && <span className="ml-2 text-xs text-green-600">✓ Connected</span>}
                        </button>
                        <button
                          onClick={() => handleGSCConnect(getCurrentStore().id)}
                          className={`text-sm ${connectionStatus.gsc ? 'text-green-600 hover:text-green-800' : 'text-indigo-600 hover:text-indigo-800'} text-left flex items-center`}
                          disabled={isLoading.gsc || !getCurrentStore()}
                        >
                          {isLoading.gsc && <span className="mr-2 animate-spin">⟳</span>}
                          {connectionStatus.gsc ? 'Reconnect Google Search Console' : 'Connect Google Search Console'}
                          {connectionStatus.gsc && <span className="ml-2 text-xs text-green-600">✓ Connected</span>}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* GA Properties Selection */}
                {getCurrentStore() && connectionStatus.ga && (
                  <div className="mb-4 bg-blue-50 p-3 rounded border border-blue-200">
                    <div className="flex items-center mb-2">
                      <i className="fas fa-chart-line text-blue-500 mr-2"></i>
                      <h4 className="text-sm font-semibold text-gray-800">Google Analytics Property</h4>
                    </div>
                    
                    {/* ADDED: Debug button */}
                    <button 
                      onClick={() => {
                        debugGAProperties();
                        alert('Check console for debug info. Refreshing GA properties...');
                      }}
                      className="text-xs text-blue-600 mb-2"
                    >
                      Debug GA Properties
                    </button>
                    
                    {isLoading.ga ? (
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="animate-spin mr-2">⟳</span> Loading properties...
                      </div>
                    ) : gaProperties[getCurrentStore().id]?.length > 0 ? (
                      <>
                        <p className="text-xs text-gray-600 mb-2">Select which Google Analytics 4 property to use for this store:</p>
                        <select
                          value={selectedGA[getCurrentStore().id] || ''}
                          onChange={e => setSelectedGA(prev => ({ ...prev, [getCurrentStore().id]: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-2"
                        >
                          <option value="">Select GA4 Property</option>
                          {gaProperties[getCurrentStore().id].map(prop => (
                            <option key={prop.id} value={prop.id.replace('properties/', '')}>
                              {prop.displayName || prop.name || prop.id}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleSaveGA(getCurrentStore().id)}
                          className="w-full bg-blue-600 text-white py-2 px-3 rounded hover:bg-blue-700 text-sm flex items-center justify-center"
                          disabled={!selectedGA[getCurrentStore().id] || isLoading.saveGA}
                        >
                          {isLoading.saveGA ? (
                            <>
                              <span className="animate-spin mr-2">⟳</span> Saving...
                            </>
                          ) : (
                            'Save Analytics Selection'
                          )}
                        </button>
                      </>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">No Google Analytics properties found. Make sure you have GA4 properties set up in your Google account.</p>
                        {/* ADDED: Force refresh button */}
                        <button
                          onClick={() => fetchGAProperties(getCurrentStore().id)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm"
                        >
                          Refresh Properties
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* GSC Sites Selection */}
                {getCurrentStore() && connectionStatus.gsc && (
                  <div className="mb-4 bg-green-50 p-3 rounded border border-green-200">
                    <div className="flex items-center mb-2">
                      <i className="fas fa-search text-green-500 mr-2"></i>
                      <h4 className="text-sm font-semibold text-gray-800">Search Console Site</h4>
                    </div>
                    
                    {isLoading.gsc ? (
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="animate-spin mr-2">⟳</span> Loading sites...
                      </div>
                    ) : gscSites[getCurrentStore().id]?.length > 0 ? (
                      <>
                        <p className="text-xs text-gray-600 mb-2">Select which Search Console site to use for this store:</p>
                        <select
                          value={selectedGSC[getCurrentStore().id] || ''}
                          onChange={e => setSelectedGSC(prev => ({ ...prev, [getCurrentStore().id]: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-2"
                        >
                          <option value="">Select Search Console Site</option>
                          {gscSites[getCurrentStore().id].map(site => (
                            <option key={site.siteUrl} value={site.siteUrl}>
                              {site.siteUrl}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleSaveGSC(getCurrentStore().id)}
                          className="w-full bg-green-600 text-white py-2 px-3 rounded hover:bg-green-700 text-sm flex items-center justify-center"
                          disabled={!selectedGSC[getCurrentStore().id] || isLoading.saveGSC}
                        >
                          {isLoading.saveGSC ? (
                            <>
                              <span className="animate-spin mr-2">⟳</span> Saving...
                            </>
                          ) : (
                            'Save Search Console Selection'
                          )}
                        </button>
                      </>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">No Search Console sites found. Make sure you have sites verified in your Google Search Console.</p>
                        {/* ADDED: Force refresh button */}
                        <button
                          onClick={() => fetchGSCSites(getCurrentStore().id)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm"
                        >
                          Refresh Sites
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Other API integrations */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex items-center">
                    <div className="w-10 h-10 flex-shrink-0 bg-white rounded-md flex items-center justify-center">
                      <i className="fab fa-google text-[#4285F4] text-2xl"></i>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-gray-800">Google Keyword Planner</h4>
                      <p className="text-xs text-gray-500">Not connected</p>
                    </div>
                  </div>
                  <button className="text-xs text-indigo-600 hover:text-indigo-800">Connect</button>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex items-center">
                    <div className="w-10 h-10 flex-shrink-0 bg-white rounded-md flex items-center justify-center">
                      <i className="fab fa-microsoft text-[#00a4ef] text-2xl"></i>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-gray-800">Bing Webmaster Tools</h4>
                      <p className="text-xs text-gray-500">Not connected</p>
                    </div>
                  </div>
                  <button className="text-xs text-indigo-600 hover:text-indigo-800">Connect</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sync Settings Card */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
        <h3 className="text-lg font-medium text-gray-800 mb-4">Sync Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Sync Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sync Frequency</label>
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option>Every 15 minutes</option>
              <option>Every hour</option>
              <option>Every 6 hours</option>
              <option>Daily</option>
            </select>
          </div>
          {/* Data Retention */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Retention</label>
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option>1 Month</option>
              <option>3 Months</option>
              <option>6 Months</option>
              <option>1 Year</option>
            </select>
          </div>
          {/* Backup Versions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Backup Versions</label>
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option>Last 5 versions</option>
              <option>Last 10 versions</option>
              <option>Last 15 versions</option>
              <option>Last 20 versions</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex flex-col space-y-3">
          {/* Checkboxes */}
          <div className="flex items-center">
            <input 
              type="checkbox" 
              id="sync-products" 
              defaultChecked 
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" 
            />
            <label htmlFor="sync-products" className="ml-2 text-sm text-gray-700">Sync product information</label>
          </div>
          <div className="flex items-center">
            <input 
              type="checkbox" 
              id="sync-keywords" 
              defaultChecked 
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" 
            />
            <label htmlFor="sync-keywords" className="ml-2 text-sm text-gray-700">Sync keyword performance</label>
          </div>
          <div className="flex items-center">
            <input 
              type="checkbox" 
              id="sync-traffic" 
              defaultChecked 
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" 
            />
            <label htmlFor="sync-traffic" className="ml-2 text-sm text-gray-700">Sync traffic data</label>
          </div>
          <div className="flex items-center">
            <input 
              type="checkbox" 
              id="auto-optimize" 
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" 
            />
            <label htmlFor="auto-optimize" className="ml-2 text-sm text-gray-700">Enable automatic keyword optimization (with backup)</label>
          </div>
        </div>
        <div className="mt-6">
          {/* Buttons */}
          <button className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors duration-200">
            Save Settings
          </button>
          <button className="ml-3 bg-white text-indigo-600 border border-indigo-600 py-2 px-4 rounded-md hover:bg-indigo-50 transition-colors duration-200">
            Force Sync Now
          </button>
        </div>
      </div>
    </>
  );
};

export default StoreConnectionPage;